/**
 * Notification Templates Hooks
 * React Query hooks for notification template management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationTemplatesApi } from '@/lib/api';
import type {
  NotificationTemplateCreate,
  NotificationTemplateUpdate,
  SendTestRequest,
} from '../types';

const TEMPLATES_KEY = ['notification-templates'];
const CHANNELS_KEY = ['notification-channels'];
const ACTIONS_KEY = ['notification-actions'];

// Fetch all templates
export function useNotificationTemplates(includeInactive = false) {
  return useQuery({
    queryKey: [...TEMPLATES_KEY, { includeInactive }],
    queryFn: () => notificationTemplatesApi.list(includeInactive),
  });
}

// Fetch single template
export function useNotificationTemplate(name: string) {
  return useQuery({
    queryKey: [...TEMPLATES_KEY, name],
    queryFn: () => notificationTemplatesApi.get(name),
    enabled: !!name,
  });
}

// Fetch available channels
export function useNotificationChannels() {
  return useQuery({
    queryKey: CHANNELS_KEY,
    queryFn: () => notificationTemplatesApi.listChannels(),
    staleTime: Infinity, // Channels don't change
  });
}

// Fetch available actions
export function useNotificationActions() {
  return useQuery({
    queryKey: ACTIONS_KEY,
    queryFn: () => notificationTemplatesApi.listActions(),
    staleTime: Infinity, // Actions don't change
  });
}

// Create template mutation
export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: NotificationTemplateCreate) => notificationTemplatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

// Update template mutation
export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, data }: { name: string; data: NotificationTemplateUpdate }) =>
      notificationTemplatesApi.update(name, data),
    onSuccess: (_, { name }) => {
      queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
      queryClient.invalidateQueries({ queryKey: [...TEMPLATES_KEY, name] });
    },
  });
}

// Delete template mutation
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => notificationTemplatesApi.delete(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

// Refresh cache mutation
export function useRefreshTemplateCache() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationTemplatesApi.refreshCache(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

// Send test notification mutation
export function useSendTestNotification() {
  return useMutation({
    mutationFn: (request: SendTestRequest) => notificationTemplatesApi.sendTest(request),
  });
}
